import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { Subscription, interval } from 'rxjs';
import { take } from 'rxjs/operators';

import { CapicheMaterialModule } from 'capiche-material';
import { UserService, User } from 'capiche-auth';
import { CapicheLoggerService } from 'capiche-logger';
import { CapicheMixpanelService } from 'capiche-mixpanel';

import { CapicheSubscriptionService } from '../../services/capiche-subscription.service';
import { StripeApiService } from '../../services/stripe-api.service';
import { ProductPriceComponent } from '../../components/product-price/product-price.component';
import { ProductViewModel } from '../../models/product-view.model';

@Component({
  selector: 'contact-support-dialog',
  imports: [CommonModule, CapicheMaterialModule],
  template: `
    <h1 mat-dialog-title>Subscription Error</h1>
    <div mat-dialog-content>
      <p>We are very sorry, there was an error processing your subscription.</p>
      <p>Please use the green button at the bottom-right to contact support for assistance.</p>
      <p>We'll sort it out for you and be in touch. Thank you for your patience.</p>
    </div>
    <div mat-dialog-actions>
      <button mat-button mat-dialog-close>Close</button>
    </div>
  `,
})
export class ContactSupportDialog {}

@Component({
  selector: 'app-subscribe-page',
  imports: [CommonModule, CapicheMaterialModule, ContactSupportDialog, ProductPriceComponent],
  templateUrl: './subscribe-page.component.html',
  styleUrl: './subscribe-page.component.css',
})
export class SubscribePageComponent implements OnDestroy, OnInit {
  subscribeService: CapicheSubscriptionService = inject(CapicheSubscriptionService);

  logger = new CapicheLoggerService('subscribe-page.component:', false);
  user: User | null = null;

  productPriceId: string = this.subscribeService.config.stripePriceId;
  productData: ProductViewModel | null | undefined = undefined;
  loadingFlag: boolean = false;
  checkoutUrl: string | null = null;

  constructor(
    private stripeApiService: StripeApiService,
    private userService: UserService,
    private dialog: MatDialog,
    private mixpanel: CapicheMixpanelService,
  ) {
    this.logger.log('constructor:', this.userService.currentUser$);
    this.userService.currentUser$.subscribe((user) => {
      this.user = user;
      if (user) this.getProductData(this.productPriceId);
      this.logger.log('userService.currentUser$.subscribe:', this.user, this.productData);
    });
  }

  ngOnInit() {
    this.mixpanel.track('subscribe-page.component: ngOnInit');

    this.subscribeService.checkoutUrl$.subscribe((urlOrNull) => {
      this.checkoutUrl = urlOrNull; // Update local state based on the observable
    });
  }

  ngOnDestroy() {
    this.mixpanel.track('subscribe-page.component: ngOnDestroy');
    // Ensure to unsubscribe when component is destroyed
    if (this.progressSubscription) {
      this.progressSubscription.unsubscribe();
    }
  }

  subscribeOnClick(e: ProductViewModel) {
    this.logger.log('subscribeOnClick:', e, this.user);
    this.startProgress();
    this.loadingFlag = true; // loading the checkout page
    if (e && this.user) {
      this.stripeApiService
        .getCheckoutUrl({
          uid: this.user.uid,
          priceId: e.price_id,
        })
        .then((url) => {
          this.subscribeService.beginCheckoutFlow(url);
          this.loadingFlag = false; // checkout page is loaded
          if (url) {
            const openUrl = this.subscribeService.config.openUrlHandler ?? ((url) => (window.location.href = url));
            this.logger.log('subscribeOnClick:', openUrl);
            openUrl(url);
          } else {
            this.logger.log('No URL returned from getCheckoutUrl');
            this.logger.log('Opening contact support dialog');
            const dialogRef = this.dialog.open(ContactSupportDialog);

            dialogRef.afterClosed().subscribe(() => {
              this.logger.log('The dialog was closed');
            });
          }
        });
    }
  }

  promoOnClick() {
    const promotionUrl = 'https://www.capiche-limited.com/early-access-registration';
    this.logger.log('promoOnClick:', promotionUrl);
    const openUrl = this.subscribeService.config.openUrlHandler ?? ((url) => (window.location.href = url));
    openUrl(promotionUrl);
  }

  checkoutOnClick() {
    this.logger.log('checkoutOnClick:', this.checkoutUrl);
    if (!this.checkoutUrl) return;
    const openUrl = this.subscribeService.config.openUrlHandler ?? ((url) => (window.location.href = url));
    openUrl(this.checkoutUrl);
  }

  async getProductData(priceId: string) {
    this.logger.log('getProductData:', priceId);
    try {
      this.logger.log('getProductData: calling stripeApiService.getProductFromPriceId');
      this.productData = undefined;
      const product: ProductViewModel | null = await this.stripeApiService.getProductFromPriceId(priceId);
      this.logger.log('getProductData: product:', product);
      this.productData = product;
    } catch (error) {
      this.logger.log('getProductData: error:', error);
      this.productData = null;
    }
  }

  progressValue: number = 0;
  private progressSubscription: Subscription | null = null;

  startProgress() {
    // Reset progress value
    this.progressValue = 0;

    // Unsubscribe from previous subscription if exists
    if (this.progressSubscription) {
      this.progressSubscription.unsubscribe();
    }

    // Create an interval that emits 60 times, once per second
    this.progressSubscription = interval(1000)
      .pipe(take(120))
      .subscribe(() => {
        // Increment progress value
        this.progressValue += 100 / 120;

        // Ensure progress value doesn't exceed 100
        if (this.progressValue > 100) {
          this.progressValue = 100;
          this.progressSubscription?.unsubscribe();
        }
      });
  }
}
